import React, { memo, useEffect, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import { Box, Grid, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import FormTextField from './FormTextField';
import FormPhoneField from './FormPhoneField';
import FormSelectField from './FormSelectField';
import { COUNTRIES, PHONE_CODES } from 'legacyfrontend/constants/countries';
import { useTemplateConfig } from 'legacyfrontend/hooks/useTemplateConfig';
import { emergencyContactSchema } from './validationSchema';

const PAIS_ID_TO_COUNTRY_CODE = {
  1: 'AR',
  2: 'PE',
  3: 'BR',
  4: 'MX',
};

const defaultInitialValues = {
  name: '',
  lastName: '',
  phone: {
    codigo: '+54',
    numero: '',
  },
  country: 'AR',
};

const EmergencyContactSection = forwardRef(
  ({ updateEmergencyContact, index, initialValues, disabled, showEmergencyContact }, ref) => {
    const { config, paisId } = useTemplateConfig();
    const defaultPhoneCode = config?.phonePrefix || '+54';
    const templateCountryCode = PAIS_ID_TO_COUNTRY_CODE[paisId] || 'AR';
    const defaultInitialValuesWithPhoneCode = useMemo(() => ({
      name: '',
      lastName: '',
      phone: {
        codigo: defaultPhoneCode,
        numero: '',
      },
      country: templateCountryCode,
    }), [defaultPhoneCode, templateCountryCode]);
    
    const formik = useFormik({
      initialValues: initialValues || defaultInitialValuesWithPhoneCode,
      validationSchema: emergencyContactSchema,
      validateOnMount: false,
      validateOnChange: false,
      validateOnBlur: true,
    });

    useEffect(() => {
      if (!showEmergencyContact) {
        formik.resetForm();
        updateEmergencyContact(index, null, false);
      } else if (initialValues) {
        formik.setValues(initialValues);
      }
    }, [showEmergencyContact, index, initialValues, formik, updateEmergencyContact]);

    useImperativeHandle(ref, () => ({
      validate: async () => {
        Object.keys(formik.values).forEach((field) => {
          formik.setFieldTouched(field, true);

          if (formik.values[field] && typeof formik.values[field] === 'object' && !Array.isArray(formik.values[field])) {
            Object.keys(formik.values[field]).forEach((nestedField) => {
              formik.setFieldTouched(`${field}.${nestedField}`, true);
            });
          }
        });

        const errors = await formik.validateForm();
        const hasErrors = Object.keys(errors).length > 0;

        const emergencyContactData = {
          ...formik.values,
          country: formik.values.country || templateCountryCode,
        };
        updateEmergencyContact(index, emergencyContactData, hasErrors);

        return !hasErrors;
      },
      getValues: () => formik.values,
      getErrors: () => formik.errors,
    }));

    const handleFieldChange = useCallback((e) => {
      formik.handleChange(e);
    }, [formik]);

    const handleBlur = useCallback((e) => {
      formik.handleBlur(e);
      formik.validateField(e.target.name);
    }, [formik]);

    const shouldShowError = useCallback(
      (fieldName) => {
        const getTouched = () => {
          if (fieldName.includes('.')) {
            return fieldName.split('.').reduce((obj, key) => obj?.[key], formik.touched);
          }
          return formik.touched[fieldName];
        };

        const getError = () => {
          if (fieldName.includes('.')) {
            return fieldName.split('.').reduce((obj, key) => obj?.[key], formik.errors);
          }
          return formik.errors[fieldName];
        };

        const isTouched = getTouched();
        const error = getError();
        return isTouched && Boolean(error);
      },
      [formik.touched, formik.errors]
    );

    const getErrorMessage = useCallback(
      (fieldName) => {
        if (shouldShowError(fieldName)) {
          if (fieldName.includes('.')) {
            return fieldName.split('.').reduce((obj, key) => obj?.[key], formik.errors);
          }
          return formik.errors[fieldName];
        }
        return '';
      },
      [shouldShowError, formik.errors]
    );

    useEffect(() => {
      if (!showEmergencyContact) return;
      const hasValues = Object.values(formik.values).some((value) =>
        typeof value === 'string'
          ? value.trim() !== ''
          : typeof value === 'object' && value !== null
            ? Object.values(value).some((v) => v && v.toString().trim() !== '')
            : false
      );

      if (hasValues) {
        const emergencyContactData = {
          ...formik.values,
          country: formik.values.country || templateCountryCode,
        };
        const hasErrors = Object.keys(formik.errors).length > 0;
        updateEmergencyContact(index, emergencyContactData, hasErrors);
      } else {
        updateEmergencyContact(index, null, false);
      }
    }, [
      formik.values,
      formik.errors,
      showEmergencyContact,
      index,
      templateCountryCode,
      updateEmergencyContact,
    ]);

    return (
      <Box sx={{ p: 3, pt: 1, bgcolor: 'white', borderRadius: 1, width: '100%' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormTextField
                  disabled={disabled}
                  error={shouldShowError('name')}
                  formik={formik}
                  helperText={getErrorMessage('name')}
                  label='Nombre'
                  name='name'
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                  placeholder='Ingrese el nombre'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormTextField
                  name='lastName'
                  label='Apellidos'
                  placeholder='Ingrese los apellidos'
                  formik={formik}
                  disabled={disabled}
                  error={shouldShowError('lastName')}
                  helperText={getErrorMessage('lastName')}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormPhoneField
              name='phone'
              label='Teléfono'
              formik={formik}
              disabled={disabled}
              codigosPais={PHONE_CODES}
              onChange={handleFieldChange}
              onBlur={handleBlur}
              codigoWidth='40%'
              numeroWidth='60%'
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSelectField
              disabled={disabled}
              formik={formik}
              label='País'
              name='country'
              onChange={handleFieldChange}
              onBlur={handleBlur}
            >
              {COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </FormSelectField>
          </Grid>
        </Grid>
      </Box>
    );
  }
);

EmergencyContactSection.displayName = 'EmergencyContactSection';

export default memo(EmergencyContactSection);
